import { usePatientContacts } from '../../hooks/store/usePatientContacts';
import { useCareTeamMembers } from '../../hooks/store/useCareTeamMembers';
import { CareTeamMember } from '../../type';
import {
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import { displayFullName, nameInitials } from '@xolv-mybrightlink/common';
import { FormattedMessage } from 'react-intl';

type Supervisor = {
  sfId: string;
  firstName?: string | null;
  lastName?: string | null;
  roles: CareTeamMember['role'][];
};

const supervisorRanks: { [key in CareTeamMember['role']]: number } = {
  'Clinical Manager': 1,
  'Program Supervisor': 2,
  Assessor: 3,
};

const rankSupervisor = (supervisor: Supervisor): number => {
  return supervisor.roles
    .map((role) => supervisorRanks[role] ?? 0)
    .reduce((sum, rank) => sum + rank, 0);
};

export const ProviderChannelMembers = (props: { channelGuid: string }) => {
  const { channelGuid } = props;
  const supervisors = useCareTeamMembers(channelGuid);
  const contacts = usePatientContacts(channelGuid);

  const supervisorBySfId = supervisors.reduce((result, supervisor) => {
    const { sfId, firstName, lastName, role } = supervisor;
    const member = result[sfId] ?? { sfId, firstName, lastName, roles: [] };
    member.roles.push(role);
    result[sfId] = member;
    return result;
  }, {} as { [sfId: string]: Supervisor });

  const sortedSupervisors = Object.values(supervisorBySfId).sort((s1, s2) => {
    return rankSupervisor(s1) - rankSupervisor(s2);
  });

  return (
    <>
      <Typography sx={{ fontSize: 14, ml: 5, my: 5, color: '#626a7a' }}>
        <FormattedMessage id='care-team.members' />
      </Typography>
      {sortedSupervisors.map((supervisor) => {
        const fullName = displayFullName(supervisor);
        const roles = supervisor.roles.join(', ');
        const name = roles ? `${fullName} (${roles})` : fullName;
        return (
          <ListItem key={supervisor.sfId}>
            <ListItemAvatar sx={{ minWidth: 'unset', pr: 2, fontWeight: 600 }}>
              <Avatar
                sx={{ bgcolor: '#626a7a', fontSize: 14, width: 30, height: 30 }}
              >
                {nameInitials(supervisor)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={<Typography sx={{ fontSize: 14 }}>{name}</Typography>}
            />
          </ListItem>
        );
      })}
      {contacts.map((contact) => {
        return (
          <ListItem key={contact.guid}>
            <ListItemAvatar sx={{ minWidth: 'unset', pr: 2, fontWeight: 600 }}>
              <Avatar
                sx={{ bgcolor: '#626a7a', fontSize: 14, width: 30, height: 30 }}
              >
                {nameInitials(contact)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography sx={{ fontSize: 14 }}>
                  {displayFullName(contact)}
                </Typography>
              }
            />
          </ListItem>
        );
      })}
    </>
  );
};
